/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button, Typography } from '@mui/material';
import { formatAccountNumber, displayAccountNumber } from '../../utils/formatUtils';
import '../../styles/EditApplicationModal.css';
import ClockSelect from '../shared/ClockSelect';

axios.defaults.withCredentials = true;

const EditApplicationModal = ({ application, user, onClose, onSave }) => {
  const [projectTitle, setProjectTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [personnel, setPersonnel] = useState([]);
  const [experimentConditions, setExperimentConditions] = useState([]);
  const [experimentDetails, setExperimentDetails] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  // Project type = the kind of compartment the project needs; narrows the
  // facility list. Same behaviour as NewApplication.
  const [projectType, setProjectType] = useState('');
  const [compartmentTypes, setCompartmentTypes] = useState([]);
  const [typeFacilityIds, setTypeFacilityIds] = useState(null);
  const projectTypeTouched = useRef(false);
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [waterOptions, setWaterOptions] = useState([]);
  const [substrateOptions, setSubstrateOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectTimeline, setProjectTimeline] = useState('');
  const [experimentalDesignSummary, setExperimentalDesignSummary] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [usesTransgenicPlants, setUsesTransgenicPlants] = useState(false);
  const [usesPathogen, setUsesPathogen] = useState(false);
  const [buaNumber, setBuaNumber] = useState('');
  const [safetyTrainingVerified, setSafetyTrainingVerified] = useState(false);
  const [funding, setFunding] = useState([]);
  const [accountNumber, setAccountNumber] = useState('');
  const [sourceOfFunds, setSourceOfFunds] = useState('');
  const [selectedUserForAccount, setSelectedUserForAccount] = useState('');
  const [selectedUserAccountNumbers, setSelectedUserAccountNumbers] = useState([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState('');
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [loadingUserAccounts, setLoadingUserAccounts] = useState(false);
  const [newAccountsToSave, setNewAccountsToSave] = useState([]);
  const [showAddAccountForm, setShowAddAccountForm] = useState(false);
  const [isPINCSU, setIsPINCSU] = useState(false);
  const [potOptions, setPotOptions] = useState([]);
  const [pots, setPots] = useState([{ pot_size: '', quantity: '' }]);
  const [numberOfCarts, setNumberOfCarts] = useState('');
  const [preferredSpace, setPreferredSpace] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // An application that the admin sent back for changes. Resubmitting it should
  // move it to "Revised" so it lands back in the admin review queue distinctly.
  const isRevisionApp = application?.project_status === 'Revision Required' || application?.project_status === 'Revised';

  useEffect(() => {
  }, [personnel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facilitiesRes, departmentsRes, rolesRes] = await Promise.all([
          axios.get('/api/facilities'),
          axios.get('/api/departments'),
          axios.get('/api/project-roles')
        ]);
        setFacilities(facilitiesRes.data);
        setDepartments(departmentsRes.data);
        setProjectRoles(rolesRes.data);
        setLoading(false);
        axios.get('/api/compartment-types')
          .then(res => setCompartmentTypes(res.data))
          .catch(err => console.error('Error loading compartment types:', err));
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load necessary data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (personnel.length === 0 && user && projectRoles.length > 0) {
          const piRole = projectRoles.find(r => r.role === 'PI');
          if (!piRole) {
            console.error('PI role not found in project roles');
            return;
          }
          setPersonnel([{
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            department: user.department?._id || user.department || '',
            role: piRole._id,
            isDefault: false
          }]);
        }
      } catch (error) {
        console.error('Error setting user data:', error);
        setError('Failed to load user data');
      }
    };

    fetchUserData();
  }, [user, personnel.length, projectRoles]);

  useEffect(() => {
    if (application && projectRoles.length > 0) {
      // Format personnel data correctly when setting initial state
      const formattedPersonnel = application.users.map(user => {
        // Handle role conversion properly
        let roleId = user.role;
        
        // If role is a populated object (from backend populate), use its _id
        if (user.role && typeof user.role === 'object' && user.role._id) {
          roleId = user.role._id;
        }
        // If role is a string (role name), find the corresponding ObjectId
        else if (typeof user.role === 'string' && user.role !== '') {
          const roleObj = projectRoles.find(r => r.role === user.role);
          roleId = roleObj ? roleObj._id : user.role;
        }
        
        return {
          firstName: user.first_name || user.firstName,
          lastName: user.last_name || user.lastName,
          email: user.email,
          department: user.customDepartment ? 'not_listed' : (user.department?._id || user.department),
          customDepartment: user.customDepartment || '',
          role: roleId,
          isDefault: user.isDefault || false,
          existingUser: user.isExistingUser,
          is_ncsu: user.is_ncsu
        };
      });

      setProjectTitle(application.project_title || '');
      setStartDate(application.project_start_date?.split('T')[0] || '');
      setEndDate(application.project_end_date?.split('T')[0] || '');
      setPersonnel(formattedPersonnel);
      setSelectedFacility(application.facility?._id || application.facility || '');
      setProjectType(application.compartment_type?._id || application.compartment_type || '');
      
      // Properly set experiment conditions - convert from database format to form format
      setExperimentConditions(application.experiment_conditions?.map(condition => {
        // Parse photo_on time (e.g., "06:00 AM" -> {hour: "06", minute: "00", period: "AM"})
        const parseTime = (timeStr) => {
          if (!timeStr) return { hour: '01', minute: '00', period: 'AM' };
          if (timeStr === 'N/A') return { hour: 'N/A', minute: 'N/A', period: 'N/A' };
          const [time, period] = timeStr.split(' ');
          const [hour, minute] = time.split(':');
          return { hour, minute, period: period || 'AM' };
        };
        
        const photoOn = parseTime(condition.photo_on);
        const photoOff = parseTime(condition.photo_off);
        const nightInterruptOn = parseTime(condition.night_interrupt_on);
        const nightInterruptOff = parseTime(condition.night_interrupt_off);
        
        return {
          photoOnHour: photoOn.hour,
          photoOnMinute: photoOn.minute,
          photoOnPeriod: photoOn.period,
          photoOffHour: photoOff.hour,
          photoOffMinute: photoOff.minute,
          photoOffPeriod: photoOff.period,
          nightInterruptOnHour: nightInterruptOn.hour,
          nightInterruptOnMinute: nightInterruptOn.minute,
          nightInterruptOnPeriod: nightInterruptOn.period,
          nightInterruptOffHour: nightInterruptOff.hour,
          nightInterruptOffMinute: nightInterruptOff.minute,
          nightInterruptOffPeriod: nightInterruptOff.period,
          tempDay: condition.temp_day || '',
          tempNight: condition.temp_night || '',
          co2Day: condition.co2_day || '',
          co2Night: condition.co2_night || '',
          humidityDay: condition.humidity_day || '',
          humidityNight: condition.humidity_night || ''
        };
      }) || []);

      // Properly set experiment details
      setExperimentDetails(application.experiment_details?.length > 0 
        ? application.experiment_details.map(detail => ({
            water: detail.water?._id || detail.water || '',
            substrate: detail.substrate?._id || detail.substrate || '',
            species: detail.species_not_listed ? 'not_listed' : (detail.species?._id || detail.species || ''),
            speciesNotListed: detail.species_not_listed || ''
          }))
        : [{ water: '', substrate: '', species: '', speciesNotListed: '' }]);

      setProjectTimeline(application.project_timeline || '');
      setExperimentalDesignSummary(application.experimental_design_summary || '');
      setSpecialRequirements(application.special_environmental_requirements || '');
      setAdditionalComments(application.additional_comments || '');
      setUsesTransgenicPlants(application.uses_transgenic_plants || false);
      setUsesPathogen(application.uses_pathogen || false);
      setBuaNumber(application.bua_number || '');
      setSafetyTrainingVerified(application.safety_training_verified || false);
      // Enhance funding with user display info from application.users
      const enhancedFunding = (application.funding || []).map(fund => {
        const projectUser = fund.user_email 
          ? application.users.find(u => u.email === fund.user_email)
          : (fund.user_id ? application.users.find(u => (u.user?._id || u.user)?.toString() === fund.user_id?.toString()) : null);
        const userName = fund.user_name || (projectUser ? `${projectUser.first_name || projectUser.firstName} ${projectUser.last_name || projectUser.lastName}` : null);
        const userEmail = fund.user_email || (projectUser ? projectUser.email : null);
        return { ...fund, user_name: userName, user_email: userEmail };
      });
      setFunding(enhancedFunding);
      // Handle both old single pot format and new multiple pots format
      if (application.pots && application.pots.length > 0) {
        // New format with multiple pots
        setPots(application.pots.map(pot => ({
          pot_size: pot.pot_size?._id || pot.pot_size || '',
          quantity: pot.quantity || ''
        })));
      } else if (application.number_of_pots || application.pot_size) {
        // Legacy format - convert to new format
        setPots([{
          pot_size: application.pot_size?._id || application.pot_size || '',
          quantity: application.number_of_pots || ''
        }]);
      } else {
        // No pot data
        setPots([{ pot_size: '', quantity: '' }]);
      }
      setNumberOfCarts(application.number_of_carts || '');
      setPreferredSpace(application.preferred_space || '');

      // Fetch facility resources if facility is selected
      if (application.facility?._id || application.facility) {
        fetchFacilityResources(application.facility?._id || application.facility);
      }
    }
  }, [application, projectRoles]);

  useEffect(() => {
    const fetchUserDetails = async (email) => {
      try {
        const response = await axios.get(`/api/users/profile/by-email/${email}`);
        return response.data.is_ncsu;
      } catch (error) {
        console.error('Error fetching user details:', error);
        return false;
      }
    };

    const checkPIStatus = async () => {
      const pi = personnel.find(p => p.role === 'PI');
      if (pi?.email) {
        const isNCSU = await fetchUserDetails(pi.email);
        setIsPINCSU(isNCSU);
      }
    };

    if (personnel.length > 0) {
      checkPIStatus();
    }
  }, [personnel]);

  const handlePersonnelChange = async (index, field, value) => {
    const updatedPersonnel = [...personnel];
    updatedPersonnel[index][field] = value;
    
    // If the email field was changed and contains a valid email, check if user exists
    if (field === 'email' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setIsCheckingEmail(true);
      try {
        const response = await axios.get(`/api/users/check-email/${value}`);
        if (response.data.exists) {
          // User exists, pre-fill data
          updatedPersonnel[index] = {
            ...updatedPersonnel[index],
            firstName: response.data.user.first_name,
            lastName: response.data.user.last_name,
            department: response.data.user.department,
            existingUser: true,
            userId: response.data.user._id
          };
          toast.success(`User ${value} found and details added`);
        } else {
          // User doesn't exist, mark as non-existing
          updatedPersonnel[index] = {
            ...updatedPersonnel[index],
            existingUser: false,
            userId: null
          };
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setIsCheckingEmail(false);
      }
    }
    
    setPersonnel(updatedPersonnel);
    setHasChanges(true);
  };

  const addPersonnel = () => {
    setPersonnel([...personnel, { firstName: '', lastName: '', email: '', department: '', role: 'PI', isDefault: false }]);
    setHasChanges(true);
  };

  // One lighting time (photoOn, photoOff, nightInterruptOn, nightInterruptOff)
  // changes hour, minute and AM/PM together — ClockSelect hands over all three.
  const handleConditionTimeChange = (index, prefix, { hour, minute, period }) => {
    const updatedConditions = [...experimentConditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [`${prefix}Hour`]: hour,
      [`${prefix}Minute`]: minute,
      [`${prefix}Period`]: period
    };
    setExperimentConditions(updatedConditions);
  };

  const handleExperimentConditionChange = (index, field, value) => {
    const updatedConditions = [...experimentConditions];
    
    // Handle N/A selection logic for all sections
    if (value === 'N/A') {
      // If selecting N/A for hour, also set minute and period to N/A for the same section
      if (field === 'photoOnHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOnHour: 'N/A',
          photoOnMinute: 'N/A',
          photoOnPeriod: 'N/A'
        };
      } else if (field === 'photoOffHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOffHour: 'N/A',
          photoOffMinute: 'N/A',
          photoOffPeriod: 'N/A'
        };
      } else if (field === 'nightInterruptOnHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOnHour: 'N/A',
          nightInterruptOnMinute: 'N/A',
          nightInterruptOnPeriod: 'N/A'
        };
      } else if (field === 'nightInterruptOffHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOffHour: 'N/A',
          nightInterruptOffMinute: 'N/A',
          nightInterruptOffPeriod: 'N/A'
        };
      } else {
        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value
        };
      }
    } else {
      // If changing from N/A to a valid value, reset the section to default values
      if (field === 'photoOnHour' && updatedConditions[index].photoOnHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOnHour: value,
          photoOnMinute: '00',
          photoOnPeriod: 'AM'
        };
      } else if (field === 'photoOffHour' && updatedConditions[index].photoOffHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOffHour: value,
          photoOffMinute: '00',
          photoOffPeriod: 'AM'
        };
      } else if (field === 'nightInterruptOnHour' && updatedConditions[index].nightInterruptOnHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOnHour: value,
          nightInterruptOnMinute: '00',
          nightInterruptOnPeriod: 'AM'
        };
      } else if (field === 'nightInterruptOffHour' && updatedConditions[index].nightInterruptOffHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOffHour: value,
          nightInterruptOffMinute: '00',
          nightInterruptOffPeriod: 'AM'
        };
      } else {
        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value
        };
      }
    }
    
    setExperimentConditions(updatedConditions);
    setHasChanges(true);
  };

  const addExperimentCondition = () => {
    setExperimentConditions([...experimentConditions, {
      photoOnHour: 'N/A',
      photoOnMinute: 'N/A',
      photoOnPeriod: 'N/A',
      photoOffHour: 'N/A',
      photoOffMinute: 'N/A',
      photoOffPeriod: 'N/A',
      nightInterruptOnHour: 'N/A',
      nightInterruptOnMinute: 'N/A',
      nightInterruptOnPeriod: 'N/A',
      nightInterruptOffHour: 'N/A',
      nightInterruptOffMinute: 'N/A',
      nightInterruptOffPeriod: 'N/A',
      tempDay: '',
      tempNight: '',
      co2Day: '',
      co2Night: '',
      humidityDay: '',
      humidityNight: ''
    }]);
    setHasChanges(true);
  };

  const removeExperimentCondition = (index) => {
    const updatedConditions = experimentConditions.filter((_, i) => i !== index);
    setExperimentConditions(updatedConditions);
    setHasChanges(true);
  };

  const handleExperimentDetailsChange = (index, field, value) => {
    const updatedDetails = [...experimentDetails];
    updatedDetails[index][field] = value;
    setExperimentDetails(updatedDetails);
    setHasChanges(true);
  };

  const addExperimentDetail = () => {
    setExperimentDetails([...experimentDetails, { water: '', substrate: '', species: '', speciesNotListed: '' }]);
    setHasChanges(true);
  };

  const removeExperimentDetail = (index) => {
    const updatedDetails = experimentDetails.filter((_, i) => i !== index);
    setExperimentDetails(updatedDetails);
    setHasChanges(true);
  };

  const fetchFacilityResources = async (facilityId) => {
    try {
      const [waterRes, substrateRes, speciesRes, potsRes] = await Promise.all([
        axios.get('/api/waters'),
        axios.get('/api/substrates'),
        axios.get('/api/species'),
        axios.get('/api/pots')
      ]);

      const filteredWaters = waterRes.data.filter(
        water => water.facility?._id === facilityId
      );
      const filteredSubstrates = substrateRes.data.filter(
        substrate => substrate.facility?._id === facilityId
      );
      const filteredSpecies = speciesRes.data.filter(
        species => species.facility?._id === facilityId
      );
      const filteredPots = potsRes.data.filter(
        pot => pot.facility?._id === facilityId
      );

      setWaterOptions(filteredWaters);
      setSubstrateOptions(filteredSubstrates);
      setSpeciesOptions(filteredSpecies);
      setPotOptions(filteredPots);
    } catch (error) {
      console.error('Error fetching facility resources:', error);
      setError('Failed to load facility resources');
    }
  };

  useEffect(() => {
    if (!projectType) {
      setTypeFacilityIds(null);
      return;
    }
    let cancelled = false;
    axios.get('/api/facilities', { params: { compartment_type: projectType } })
      .then(res => { if (!cancelled) setTypeFacilityIds(res.data.map(f => f._id)); })
      .catch(err => console.error('Error filtering facilities by project type:', err));
    return () => { cancelled = true; };
  }, [projectType]);

  const facilityOptions = typeFacilityIds
    ? facilities.filter(f => typeFacilityIds.includes(f._id) || f._id === selectedFacility)
    : facilities;

  // A facility picked before the user changed the type may not offer it.
  useEffect(() => {
    if (projectTypeTouched.current && typeFacilityIds && selectedFacility && !typeFacilityIds.includes(selectedFacility)) {
      handleFacilityChange({ target: { value: '' } });
    }
    // handleFacilityChange is recreated every render; only the ids matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFacilityIds, selectedFacility]);

  const handleFacilityChange = (e) => {
    const facilityId = e.target.value;
    setSelectedFacility(facilityId);
    
    // Clear all options if no facility is selected
    if (!facilityId) {
      setWaterOptions([]);
      setSubstrateOptions([]);
      setSpeciesOptions([]);
      setPotOptions([]);
      return;
    }
    
    fetchFacilityResources(facilityId);
    setHasChanges(true);
  };

  const handleAccountNumberChange = (e) => {
    setAccountNumber(formatAccountNumber(e.target.value));
  };

  const handleSourceOfFundsChange = (e) => {
    setSourceOfFunds(e.target.value);
  };

  // Reset account form
  const resetAccountForm = () => {
    setSelectedUserForAccount('');
    setSelectedAccountNumber('');
    setAccountNumber('');
    setSourceOfFunds('');
    setIsNewAccount(false);
    setSelectedUserAccountNumbers([]);
    setShowAddAccountForm(false);
  };

  // Handle user selection for account
  const handleUserSelectionForAccount = async (email) => {
    setSelectedUserForAccount(email);
    setSelectedAccountNumber('');
    setAccountNumber('');
    setSourceOfFunds('');
    setIsNewAccount(false);
    
    if (!email) {
      setSelectedUserAccountNumbers([]);
      return;
    }
    
    try {
      setLoadingUserAccounts(true);
      const encodedEmail = encodeURIComponent(email);
      const response = await axios.get(`/api/users/with-accounts/${encodedEmail}`);
      setSelectedUserAccountNumbers(response.data.account_numbers || []);
    } catch (error) {
      console.error('Error fetching user account numbers:', error);
      setSelectedUserAccountNumbers([]);
    } finally {
      setLoadingUserAccounts(false);
    }
  };

  // Handle account number selection
  const handleAccountNumberSelection = (accountId) => {
    setSelectedAccountNumber(accountId);
    
    if (accountId === 'new') {
      setIsNewAccount(true);
      setAccountNumber('');
      setSourceOfFunds('');
    } else if (accountId) {
      const selectedAccount = selectedUserAccountNumbers.find(acc => acc._id === accountId);
      if (selectedAccount) {
        setIsNewAccount(false);
        setAccountNumber(selectedAccount.number);
        setSourceOfFunds(selectedAccount.source);
      }
    } else {
      setIsNewAccount(false);
      setAccountNumber('');
      setSourceOfFunds('');
    }
  };

  const handleAddFunding = () => {
    if (!selectedUserForAccount) {
      toast.error('Please select a user first');
      return;
    }

    if (!accountNumber || !sourceOfFunds) {
      toast.error('Please fill in both account number and source of funds');
      return;
    }

    // Check for duplicates in the current funding list
    const isDuplicate = funding.some(f => 
      f.account_number === accountNumber && f.source_of_funds === sourceOfFunds
    );

    if (isDuplicate) {
      toast.error('This funding entry already exists');
      return;
    }

    const selectedUser = personnel.find(p => p.email === selectedUserForAccount);
    
    // Create funding entry
    const newFundingEntry = { 
      account_number: accountNumber, 
      source_of_funds: sourceOfFunds,
      user_email: selectedUserForAccount,
      user_name: selectedUser ? `${selectedUser.firstName || selectedUser.first_name} ${selectedUser.lastName || selectedUser.last_name}` : selectedUserForAccount,
      is_new_account: isNewAccount || selectedAccountNumber === 'new'
    };

    // If this is a new account, track it for saving later
    if (isNewAccount || selectedAccountNumber === 'new') {
      // Track new accounts for saving to user profiles
      const newAccountToSave = {
        userEmail: selectedUserForAccount,
        accountNumber: accountNumber,
        sourceOfFunds: sourceOfFunds
      };
      
      // Check if this exact account is already in the list to save
      const alreadyTracked = newAccountsToSave.some(acc => 
        acc.userEmail === selectedUserForAccount && acc.accountNumber === accountNumber
      );
      
      if (!alreadyTracked) {
        setNewAccountsToSave([...newAccountsToSave, newAccountToSave]);
      }
    }

    setFunding([...funding, newFundingEntry]);
    
    // Reset form completely
    resetAccountForm();
    
    toast.success('Account added to project funding list');
    setHasChanges(true);
  };

  const handleRemoveFunding = (index) => {
    const removedFunding = funding[index];
    
    // If removing a new account, also remove it from the accounts to save
    if (removedFunding.is_new_account) {
      setNewAccountsToSave(newAccountsToSave.filter(acc => 
        !(acc.accountNumber === removedFunding.account_number && 
          acc.userEmail === removedFunding.user_email)
      ));
    }
    
    setFunding(funding.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  // Note: saveNewAccountsToUsers function removed - account numbers are now handled automatically by the backend

  const handleSubmit = async (status) => {
    try {
      const isSaving = status === 'Saved';
      
      // Skip validation when saving - allow incomplete forms to be saved
      if (!isSaving) {
        // Check for at least one PI in the personnel array
        const piRole = projectRoles.find(r => r.role === 'PI');
        
        if (!piRole) {
          toast.error('PI role not found');
          return;
        }
        
        const hasPI = personnel.some(person => person.role === piRole._id);
        
        if (!hasPI) {
          toast.error('Project must have at least one PI (Principal Investigator)');
          return;
        }

        // Validate funding information for NCSU users
        if (isPINCSU && funding.length === 0) {
          toast.error('Please add at least one funding source');
          return;
        }

        // Validate safety training for submission
        if (!safetyTrainingVerified) {
          toast.error('Safety Training/Biosafety Verification is required for submission');
          return;
        }

        // Validate BUA number when transgenic or pathogen is selected
        if ((usesTransgenicPlants || usesPathogen) && !buaNumber.trim()) {
          toast.error('BUA Number is required when using transgenic plants or pathogens');
          return;
        }
      }

      // Note: Account numbers are now automatically saved by the backend when creating users
      // No need to save them separately in the frontend

      const applicationData = {
        project_title: projectTitle,
        project_start_date: startDate,
        project_end_date: endDate,
        users: personnel.map(person => ({
          first_name: person.firstName || person.first_name,
          last_name: person.lastName || person.last_name,
          email: person.email,
          department: typeof person.department === 'object' ? person.department._id : person.department,
          customDepartment: person.department === 'not_listed' ? person.customDepartment : undefined,
          role: person.role,
          isDefault: person.isDefault || false,
          userId: person.userId || null,  // Store user ID if user exists
          existingUser: person.existingUser || false  // Mark if user already exists
        })),
        facility: selectedFacility,
        compartment_type: projectType || null,
        experiment_conditions: experimentConditions.map(condition => ({
          photo_on: condition.photoOnHour === 'N/A' ? 'N/A' : `${condition.photoOnHour}:${condition.photoOnMinute} ${condition.photoOnPeriod}`,
          photo_off: condition.photoOffHour === 'N/A' ? 'N/A' : `${condition.photoOffHour}:${condition.photoOffMinute} ${condition.photoOffPeriod}`,
          night_interrupt_on: condition.nightInterruptOnHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOnHour}:${condition.nightInterruptOnMinute} ${condition.nightInterruptOnPeriod}`,
          night_interrupt_off: condition.nightInterruptOffHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOffHour}:${condition.nightInterruptOffMinute} ${condition.nightInterruptOffPeriod}`,
          temp_day: condition.tempDay,
          temp_night: condition.tempNight,
          co2_day: condition.co2Day,
          co2_night: condition.co2Night,
          humidity_day: condition.humidityDay,
          humidity_night: condition.humidityNight
        })) || [],
        pots: pots.filter(pot => pot.pot_size && pot.quantity).map(pot => ({
          pot_size: pot.pot_size,
          quantity: parseInt(pot.quantity) || 0
        })),
        uses_transgenic_plants: usesTransgenicPlants,
        uses_pathogen: usesPathogen,
        bua_number: (usesTransgenicPlants || usesPathogen) ? buaNumber : undefined,
        project_timeline: projectTimeline,
        experimental_design_summary: experimentalDesignSummary,
        special_environmental_requirements: specialRequirements,
        additional_comments: additionalComments,
        safety_training_verified: safetyTrainingVerified,
        project_status: status,
        funding: funding
      };

      // Transform experiment_details before submission
      applicationData.experiment_details = experimentDetails.map(detail => ({
        water: detail.water || undefined,
        substrate: detail.substrate || undefined,
        species: detail.species === 'not_listed' ? undefined : detail.species || undefined,
        species_not_listed: detail.species === 'not_listed' ? detail.speciesNotListed : undefined
      })).filter(detail => detail.water || detail.substrate || detail.species || detail.species_not_listed);

      setSaving(true);
      const response = await axios.put(`/api/applications/${application._id}`, applicationData);
      
      // Clear new accounts array since they've been saved
      if (status === 'Submitted') {
        setNewAccountsToSave([]);
      }
      
      toast.success(`Application ${status.toLowerCase()} successfully!`);
      setHasChanges(false);
      onSave(response.data);
      onClose();

    } catch (err) {
      console.error('Error submitting application:', err);
      toast.error('Failed to submit application');
    } finally {
      setSaving(false);
    }
  };

  const handleCombinedChange = (regularChangeHandler, value) => {
    setHasChanges(true);
    regularChangeHandler(value);
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Are you sure you want to discard your changes?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'edit-modal-overlay') {
      handleCancel();
    }
  };

  if (loading) {
    return (
      <div className="edit-modal-overlay">
        <div className="edit-modal-content">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-modal-overlay">
        <div className="edit-modal-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-modal-overlay" onClick={handleOverlayClick}>
      <div className="edit-modal-content">
        <div className="edit-modal-header">
          <div className="header-info">
            <h2>Edit Application</h2>
            <div className="application-context">
              <span className="project-title">{application.project_title}</span>
              <span className={`status-badge status-${application.project_status?.toLowerCase()}`}>
                {application.project_status}
              </span>
            </div>
          </div>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>

        <div className="edit-modal-body">
          <form onSubmit={(e) => { 
            e.preventDefault(); 
            // Resubmitting an application that was sent back for revision moves it
            // to "Revised" (not a fresh "Submitted") so admins can tell it came
            // back after their requested changes.
            handleSubmit(isRevisionApp ? 'Revision Required' : 'Submitted');
          }} className="new-application-form">
            
            {compartmentTypes.length > 0 && (
              <div className="project-type-group">
                <label htmlFor="projectType">
                  Project Type: <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>(optional)</span>
                </label>
                <select
                  id="projectType"
                  value={projectType}
                  onChange={(e) => {
                    projectTypeTouched.current = true;
                    handleCombinedChange(setProjectType, e.target.value);
                  }}
                >
                  <option value="">Not specified — show all facilities</option>
                  {compartmentTypes.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
                <p className="field-hint">
                  {compartmentTypes.find(t => t._id === projectType)?.description
                    || 'The kind of space this project needs. The facility list below shows only facilities that have it.'}
                </p>
              </div>
            )}

            <label htmlFor="projectTitle">Project Title:</label>
            <input
              type="text"
              id="projectTitle"
              value={projectTitle}
              onChange={(e) => handleCombinedChange(setProjectTitle, e.target.value)}
              required
            />

            <div className="date-facility-row">
              <div className="date-group">
                <label htmlFor="startDate">Start Date:</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => handleCombinedChange(setStartDate, e.target.value)}
                  required
                />
              </div>
              <div className="date-group">
                <label htmlFor="endDate">End Date: <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>(optional)</span></label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => handleCombinedChange(setEndDate, e.target.value)}
                />
              </div>
              <div className="facility-group">
                <label htmlFor="facility">Select Facility:</label>
                <select
                  id="facility"
                  value={selectedFacility || ''}
                  onChange={handleFacilityChange}
                  required
                >
                  <option value="">Select a facility</option>
                  {facilityOptions.map((facility) => (
                    <option key={facility._id} value={facility._id}>
                      {facility.facility_name}
                    </option>
                  ))}
                </select>
                {typeFacilityIds && typeFacilityIds.length === 0 && (
                  <p className="field-hint field-hint-warning">
                    No facility offers this project type yet. Choose another type, or "Not specified".
                  </p>
                )}
              </div>
            </div>

            <h2>Please List all Personnel involved with Project.</h2>
            <div className="personnel-table">
              <table>
                <thead>
                  <tr>
                    <th>* First Name</th>
                    <th>* Last Name</th>
                    <th>* Email</th>
                    <th>* Department</th>
                    <th>* PI/Affiliate</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                {personnel.map((person, index) => (
                  <tr key={index} className={person.existingUser ? "existing-user" : person.existingUser === false ? "new-user" : ""}>
                    <td>
                      <input
                        type="text"
                        value={person.firstName || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'firstName', value),
                            e.target.value
                          );
                        }}
                        required
                        disabled={person.existingUser}
                      />
                      {isCheckingEmail && person.email && index === personnel.length - 1 && (
                        <div className="loading-indicator">Checking...</div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        value={person.lastName || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'lastName', value),
                            e.target.value
                          );
                        }}
                        required
                        disabled={person.existingUser}
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        value={person.email || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'email', value),
                            e.target.value
                          );
                        }}
                        required
                        disabled={false}
                      />
                      {person.existingUser && <span className="user-status existing">✓ Existing User</span>}
                      {person.existingUser === false && <span className="user-status new">New User</span>}
                    </td>
                    <td>
                      <select
                        value={person.department || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'department', value),
                            e.target.value
                          );
                        }}
                        required
                        disabled={false}
                      >
                        <option value="">Select department</option>
                        <option value="not_listed">Department not listed</option>
                        {[...departments].sort((a, b) => (a.department_name || '').localeCompare(b.department_name || '')).map((dept) => (
                          <option key={dept._id} value={dept._id}>
                            {dept.department_name} ({dept.department_code})
                          </option>
                        ))}
                      </select>
                      {person.department === 'not_listed' && (
                        <input
                          type="text"
                          placeholder="Enter department name"
                          value={person.customDepartment || ''}
                          onChange={(e) => {
                            handleCombinedChange(
                              (value) => handlePersonnelChange(index, 'customDepartment', value),
                              e.target.value
                            );
                          }}
                          required
                          style={{ marginTop: '5px', width: '100%' }}
                        />
                      )}
                    </td>
                    <td>
                      <select
                        value={person.role || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'role', value),
                            e.target.value
                          );
                        }}
                        required
                        disabled={false}
                      >
                        <option value="">Select role</option>
                        {projectRoles.map((role) => (
                          <option key={role._id} value={role._id}>
                            {role.role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button 
                        type="button" 
                        onClick={() => {
                          // Check if this is the last person
                          if (personnel.length === 1) {
                            toast.error('Cannot remove the last person. Please add another person first.');
                            return;
                          }
                          
                          // Check if removing this person would leave no PI
                          const remainingPersonnel = personnel.filter((_, i) => i !== index);
                          const piRole = projectRoles.find(r => r.role === 'PI');
                          
                          const hasPI = remainingPersonnel.some(p => p.role === piRole?._id);
                          
                          if (!hasPI) {
                            toast.error('Cannot remove this person. At least one PI must remain in the project.');
                            return;
                          }
                          
                          const updatedPersonnel = personnel.filter((_, i) => i !== index);
                          setPersonnel(updatedPersonnel);
                          setHasChanges(true);
                        }}
                        className="delete-personnel-btn"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
              <button type="button" onClick={addPersonnel}>Add Personnel</button>
            </div>

            <h2>Experiment Conditions</h2>
            <div className="experiment-conditions-table">
              <table>
                <thead>
                  <tr className="table-header">
                    <th colSpan="2">Photo Period</th>
                    <th colSpan="2">Night Interrupt</th>
                    <th colSpan="2">Temperature</th>
                    <th colSpan="2">CO2</th>
                    <th colSpan="2">Humidity</th>
                    <th rowSpan="2"></th>
                  </tr>
                  <tr className="table-header">
                    <th>On</th>
                    <th>Off</th>
                    <th>On</th>
                    <th>Off</th>
                    <th>Day</th>
                    <th>Night</th>
                    <th>Day</th>
                    <th>Night</th>
                    <th>Day</th>
                    <th>Night</th>
                  </tr>
                </thead>
                <tbody>
                  {experimentConditions.map((condition, index) => (
                    <tr key={index}>
                      <td>
                        <ClockSelect
                          hour={condition.photoOnHour}
                          minute={condition.photoOnMinute}
                          period={condition.photoOnPeriod}
                          defaultHour='01'
                          onChange={(parts) => handleConditionTimeChange(index, 'photoOn', parts)}
                        />
                      </td>
                      <td>
                        <ClockSelect
                          hour={condition.photoOffHour}
                          minute={condition.photoOffMinute}
                          period={condition.photoOffPeriod}
                          onChange={(parts) => handleConditionTimeChange(index, 'photoOff', parts)}
                        />
                      </td>
                      <td>
                        <ClockSelect
                          hour={condition.nightInterruptOnHour}
                          minute={condition.nightInterruptOnMinute}
                          period={condition.nightInterruptOnPeriod}
                          onChange={(parts) => handleConditionTimeChange(index, 'nightInterruptOn', parts)}
                        />
                      </td>
                      <td>
                        <ClockSelect
                          hour={condition.nightInterruptOffHour}
                          minute={condition.nightInterruptOffMinute}
                          period={condition.nightInterruptOffPeriod}
                          onChange={(parts) => handleConditionTimeChange(index, 'nightInterruptOff', parts)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.tempDay}
                          onChange={(e) => handleExperimentConditionChange(index, 'tempDay', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.tempNight}
                          onChange={(e) => handleExperimentConditionChange(index, 'tempNight', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.co2Day}
                          onChange={(e) => handleExperimentConditionChange(index, 'co2Day', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.co2Night}
                          onChange={(e) => handleExperimentConditionChange(index, 'co2Night', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.humidityDay}
                          onChange={(e) => handleExperimentConditionChange(index, 'humidityDay', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={condition.humidityNight}
                          onChange={(e) => handleExperimentConditionChange(index, 'humidityNight', e.target.value)}
                        />
                      </td>
                      <td>
                        <button type="button" onClick={() => removeExperimentCondition(index)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" onClick={addExperimentCondition}>Add Experiment Condition</button>
            </div>

            <h2>Experiment Details</h2>
            <div className="experiment-details-table">
              {!selectedFacility ? (
                <div className="no-facility-message">
                  Please select a facility first to view available resources
                </div>
              ) : (
                <table>
                  <thead>
                    <tr className="table-header">
                      <th>Water</th>
                      <th>Substrate</th>
                      <th>Species</th>
                      <th>Species Not Listed</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {experimentDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            value={detail.water}
                            onChange={(e) => handleExperimentDetailsChange(index, 'water', e.target.value)}
                            required
                          >
                            <option value="">Select Water</option>
                            {waterOptions.map((water) => (
                              <option key={water._id} value={water._id}>
                                {water.description}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            value={detail.substrate}
                            onChange={(e) => handleExperimentDetailsChange(index, 'substrate', e.target.value)}
                            required
                          >
                            <option value="">Select Substrate</option>
                            {substrateOptions.map((substrate) => (
                              <option key={substrate._id} value={substrate._id}>
                                {substrate.description}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            value={detail.species}
                            onChange={(e) => handleExperimentDetailsChange(index, 'species', e.target.value)}
                            required
                          >
                            <option value="">Select Species</option>
                            {speciesOptions.map((species) => (
                              <option key={species._id} value={species._id}>
                                {species.name} - {species.scientific_name}
                              </option>
                            ))}
                            <option value="not_listed">Species not listed</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={detail.speciesNotListed}
                            onChange={(e) => handleExperimentDetailsChange(index, 'speciesNotListed', e.target.value)}
                            placeholder={detail.species === 'not_listed' ? 'Enter species name' : 'Only if species not listed'}
                            disabled={detail.species !== 'not_listed'}
                            required={detail.species === 'not_listed'}
                          />
                        </td>
                        <td>
                          <button type="button" onClick={() => removeExperimentDetail(index)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {selectedFacility && (
                <button type="button" onClick={addExperimentDetail}>
                  Add Experiment Detail
                </button>
              )}
            </div>

            <div className="additional-experiment-details">
              <div className="form-section">
                <h4>Pot Requirements</h4>
                {pots.map((pot, index) => (
                  <div key={index} className="form-row pot-row">
                    <div className="form-group">
                      <label htmlFor={`potSize-${index}`}>Pot Size:</label>
                      <select
                        id={`potSize-${index}`}
                        value={pot.pot_size}
                        onChange={(e) => {
                          const newPots = [...pots];
                          newPots[index].pot_size = e.target.value;
                          handleCombinedChange(setPots, newPots);
                        }}
                      >
                        <option value="">Select Pot Size</option>
                        {potOptions.map((potOption) => (
                          <option key={potOption._id} value={potOption._id}>
                            {potOption.pot} - {potOption.pot_description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`quantity-${index}`}>Quantity:</label>
                      <input
                        type="number"
                        id={`quantity-${index}`}
                        min="1"
                        value={pot.quantity}
                        onChange={(e) => {
                          const newPots = [...pots];
                          newPots[index].quantity = e.target.value;
                          handleCombinedChange(setPots, newPots);
                        }}
                      />
                    </div>

                    <div className="form-group pot-actions">
                      {pots.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-pot"
                          onClick={() => {
                            const newPots = pots.filter((_, i) => i !== index);
                            handleCombinedChange(setPots, newPots);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="btn-add-pot"
                  onClick={() => {
                    const newPots = [...pots, { pot_size: '', quantity: '' }];
                    handleCombinedChange(setPots, newPots);
                  }}
                >
                  + Add Another Pot Size
                </button>
              </div>

            </div>

            <div className="additional-fields">
              <div className="text-field">
                <label htmlFor="projectTimeline">* Project Timeline including harvest and data collection:</label>
                <textarea
                  id="projectTimeline"
                  value={projectTimeline}
                  onChange={(e) => handleCombinedChange(setProjectTimeline, e.target.value)}
                  required
                />
              </div>

              <div className="text-field">
                <label htmlFor="experimentalDesign">
                  * Summary of experimental design including an explanation of cultural procedures and/or any special techniques
                  (limit to 250 words):
                </label>
                <textarea
                  id="experimentalDesign"
                  value={experimentalDesignSummary}
                  onChange={(e) => handleCombinedChange(setExperimentalDesignSummary, e.target.value)}
                  maxLength={2000}
                  required
                />
              </div>

              <div className="text-field">
                <label htmlFor="specialRequirements">Special Environmental Requirements (optional):</label>
                <textarea
                  id="specialRequirements"
                  value={specialRequirements}
                  onChange={(e) => handleCombinedChange(setSpecialRequirements, e.target.value)}
                />
              </div>

              <div className="text-field">
                <label htmlFor="additionalComments">Additional Comments (limit to 250 words):</label>
                <textarea
                  id="additionalComments"
                  value={additionalComments}
                  onChange={(e) => handleCombinedChange(setAdditionalComments, e.target.value)}
                  maxLength={2000}
                />
              </div>

              <div className="checkbox-group">
                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="transgenicPlants"
                    checked={usesTransgenicPlants}
                    onChange={(e) => handleCombinedChange(setUsesTransgenicPlants, e.target.checked)}
                  />
                  <label htmlFor="transgenicPlants">Research uses transgenic plants</label>
                </div>

                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="pathogen"
                    checked={usesPathogen}
                    onChange={(e) => handleCombinedChange(setUsesPathogen, e.target.checked)}
                  />
                  <label htmlFor="pathogen">Research uses pathogen</label>
                </div>

                {/* BUA Number field - only shown when transgenic OR pathogen is selected */}
                {(usesTransgenicPlants || usesPathogen) && (
                  <div className="bua-number-field">
                    <label htmlFor="buaNumber">* BUA Number:</label>
                    <input
                      type="text"
                      id="buaNumber"
                      value={buaNumber}
                      onChange={(e) => handleCombinedChange(setBuaNumber, e.target.value)}
                      placeholder="Enter BUA number"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="safety-verification">
                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="safetyTraining"
                    checked={safetyTrainingVerified}
                    onChange={(e) => handleCombinedChange(setSafetyTrainingVerified, e.target.checked)}
                    required
                  />
                  <label htmlFor="safetyTraining">
                    * Safety Training/Biosafety Verification
                  </label>
                </div>
                <p className="safety-text">
                  I certify that all personnel associated with our research project being conducted in the North Campus State University Phytotron and Plant Science Building, including technical staff, graduate students, undergraduate student assistants, postdoctoral associates and visiting research scientists, have received proper training in standard operating procedures of equipment, in proper handling of any chemicals involved with the research, and in the rules and regulations regarding laboratory safety, and all necessary information has been disclosed on the Transgenic Plant Research Biosafety Verification page of this proposal.
                </p>
              </div>

              {/* Account Information Section */}
              <div className="section">
                <h2>Account Information</h2>
                {funding.map((fund, index) => {
                  const displayName = fund.user_name || fund.user_email || 'Unknown User';
                  
                  return (
                    <div key={index} className="funding-entry" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div><strong>User:</strong> {displayName}</div>
                        <div><strong>Account Number:</strong> {displayAccountNumber(fund.account_number)}</div>
                        <div><strong>Source of Funds:</strong> {fund.source_of_funds}</div>
                      </div>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveFunding(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}

                {!showAddAccountForm ? (
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setShowAddAccountForm(true)}
                      style={{ height: '40px', fontSize: '16px' }}
                    >
                      + Add Account Information
                    </Button>
                  </div>
                ) : (
                  <div style={{ marginTop: '16px', padding: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0 }}>Add Account Information</h3>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={resetAccountForm}
                        style={{ minWidth: 'auto' }}
                      >
                        Cancel
                      </Button>
                    </div>
                    
                    {/* User Selection */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                        Select User:
                      </label>
                      <select
                        value={selectedUserForAccount}
                        onChange={(e) => handleUserSelectionForAccount(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '16px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          height: '40px'
                        }}
                      >
                        <option value="">Select a user from the personnel list</option>
                        {personnel.map((person, index) => (
                          <option key={index} value={person.email}>
                            {person.firstName || person.first_name} {person.lastName || person.last_name} ({person.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Account Number Selection */}
                    {selectedUserForAccount && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                          Account Number:
                        </label>
                        {loadingUserAccounts ? (
                          <div style={{ padding: '8px', fontStyle: 'italic' }}>Loading account numbers...</div>
                        ) : (
                          <select
                            value={selectedAccountNumber}
                            onChange={(e) => handleAccountNumberSelection(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '16px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              height: '40px'
                            }}
                          >
                            <option value="">Select an account number</option>
                            {selectedUserAccountNumbers.map((account) => (
                              <option key={account._id} value={account._id}>
                                {account.number} - {account.source}
                              </option>
                            ))}
                            <option value="new">+ Add New Account Number</option>
                          </select>
                        )}
                      </div>
                    )}

                    {/* Account Details Form */}
                    {(selectedUserForAccount && (isNewAccount || selectedAccountNumber === 'new' || selectedAccountNumber)) && (
                      <>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                              Account Number:
                            </label>
                            <input
                              type="text"
                              placeholder="XXXXXX-XXXXX"
                              value={accountNumber}
                              onChange={handleAccountNumberChange}
                              disabled={!isNewAccount && selectedAccountNumber !== 'new'}
                              maxLength={12}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                height: '40px',
                                backgroundColor: (!isNewAccount && selectedAccountNumber !== 'new') ? '#f5f5f5' : 'white'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                              Source of Funds:
                            </label>
                            <input
                              type="text"
                              placeholder="Enter source of funds"
                              value={sourceOfFunds}
                              onChange={handleSourceOfFundsChange}
                              disabled={!isNewAccount && selectedAccountNumber !== 'new'}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                height: '40px',
                                backgroundColor: (!isNewAccount && selectedAccountNumber !== 'new') ? '#f5f5f5' : 'white'
                              }}
                            />
                          </div>
                        </div>

                        <Button
                          variant="contained"
                          onClick={handleAddFunding}
                          disabled={!accountNumber || !sourceOfFunds}
                          style={{ height: '40px' }}
                        >
                          {isNewAccount || selectedAccountNumber === 'new' ? 'Add New Account to Project' : 'Add to Project'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Debug info */}
            {newAccountsToSave.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                <div style={{ color: '#856404', fontSize: '14px', fontWeight: 'bold' }}>
                  🔧 Debug: {newAccountsToSave.length} account(s) to save on submission
                </div>
                {newAccountsToSave.map((acc, idx) => (
                  <div key={idx} style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                    • {displayAccountNumber(acc.accountNumber)} for {acc.userEmail} (source: {acc.sourceOfFunds})
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>

        <div className="edit-modal-footer">
          <div className="footer-info">
            {hasChanges && <span className="changes-indicator">• Unsaved changes</span>}
          </div>
          <div className="footer-actions">
            <button 
              className="btn-secondary" 
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            {/* "Save" as a draft (Saved) is only for applications that haven't
                been submitted yet. Once submitted, the only editable state is
                Revision Required, where the action is Save & Resubmit (→ Revised)
                so the app stays in the review cycle, never dropping to Saved. */}
            {application.project_status === 'Saved' && (
              <>
                <button 
                  className="btn-primary" 
                  onClick={() => handleSubmit('Saved')}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  className="btn-submit" 
                  onClick={() => handleSubmit('Submitted')}
                  disabled={saving}
                >
                  {saving ? 'Submitting...' : 'Save & Submit'}
                </button>
              </>
            )}
            {isRevisionApp && (
              <button 
                className="btn-submit" 
                onClick={() => handleSubmit('Revision Required')}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditApplicationModal;